import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import './Hero.css';
import { Globe, CalendarDays, CreditCard, MessageSquare, Mail, ClipboardList, Lock, Headphones, Star, Sparkles, KeyRound, Bell, BarChart3, Users, Pill, MapPin } from 'lucide-react';
import { OnboardingSteps, FeaturesDetailed, EmotionalSection, QuickFAQ } from './LandingSections';
import CintaDeLuz from './CintaDeLuz';
import TodoConectado from './TodoConectado';
import EsquemaHero, { IdPieza, TODAS_LAS_PIEZAS } from './EsquemaHero';
import ProfessionalsShowcase from './ProfessionalsShowcase';
import RotatingWords, { RotatingWord } from './RotatingWords';
import LoadingSpinner from './LoadingSpinner';

// Interface para el usuario actualizada
interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  auth_method: string;
  picture?: string;
  isAssistant?: boolean;
  is_assistant?: boolean;
}

// Palabras del titular: "Tu link para ___". Compás parejo, con más tiempo en
// la novedad (recetas) y en el remate ("todo"). Fuera del componente para que
// no sea un array nuevo en cada render: reiniciaría el reloj de la rotación.
// pieza: la del esquema (EsquemaHero) que se enciende con la palabra; "todo"
// no tiene una, enciende todas.
const PALABRAS_TITULAR: Array<RotatingWord & { pieza?: IdPieza }> = [
  { text: 'turnos', ms: 1900, pieza: 'turnos' },
  { text: 'recordatorios', ms: 1900, pieza: 'recordatorios' },
  { text: 'cobros', ms: 1900, pieza: 'cobros' },
  { text: 'recetas electrónicas', ms: 2400, pieza: 'recetas' },
  { text: 'historias clínicas', ms: 1900, pieza: 'historias' },
  { text: 'todo', ms: 2800 },
];

// Frases del subtítulo y la palabra del titular con la que se encienden: el
// subtítulo acompaña la rotación en vez de quedarse quieto. Con "todo" (la
// última palabra) se encienden todas, y con ellas "todo desde tu link".
// Después de esa primera vuelta, las frases dejan su lugar al esquema
// (EsquemaHero), que sigue acompañando al titular. Pedido de Esteban (oct-2026).
const FRASES_SUBTITULO = [
  { texto: 'Turnos online', palabra: 0 },
  { texto: 'recordatorios', palabra: 1 },
  { texto: 'cobros con Mercado Pago', palabra: 2 },
  { texto: 'recetas electrónicas', palabra: 3 },
  { texto: 'historias clínicas', palabra: 4 },
];
const PALABRA_TODO = PALABRAS_TITULAR.length - 1;

// Precio para profesionales nuevos: el mismo que cobra el backend
// (NEW_SUBSCRIPTION_PRICE en config/pricing.js). Lo usan la tarjeta de precio
// y la sección "Todo conectado"; si cambia, se cambia acá y en el backend.
const PRECIO_MENSUAL = '$20.000';

// Titular y subtítulo del hero, aparte del resto de la home: la palabra del
// titular cambia cada ~2 s, y si ese estado viviera en Hero, cada cambio
// volvería a dibujar la home entera, no solo esta parte.
const MensajeDelHero: React.FC = () => {
  const [palabraActiva, setPalabraActiva] = useState(0);

  // Cuando el titular llega por primera vez a "todo" (a los 10 s) se encienden
  // todas las frases; un momento después se van y se arma el esquema en su
  // lugar. Con movimiento reducido quedan las frases.
  const [conEsquema, setConEsquema] = useState(false);
  useEffect(() => {
    if (conEsquema || palabraActiva !== PALABRA_TODO) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setTimeout(() => setConEsquema(true), 900);
    return () => clearTimeout(t);
  }, [palabraActiva, conEsquema]);
  const piezaDeLaPalabra = PALABRAS_TITULAR[palabraActiva]?.pieza;
  const piezasActivas = piezaDeLaPalabra ? [piezaDeLaPalabra] : TODAS_LAS_PIEZAS;
  // Ya con el esquema, las frases (invisibles) quedan quietas en "todo": si no,
  // sus subrayados se seguirían animando sin que nadie los vea.
  const fraseActiva = conEsquema ? PALABRA_TODO : palabraActiva;

  return (
    <>
      {/* Título: la última palabra rota, con el efecto que antes tenía el
          subtítulo, ahora en grande. El texto completo va aparte para
          lectores de pantalla y buscadores; lo animado es decorativo. */}
      <h1 className="hero-glow font-bold leading-[1.08] tracking-tight mb-5 sm:mb-7 px-2 text-white">
        <span className="sr-only">
          Tu link para turnos, recordatorios, cobros, recetas electrónicas e historias clínicas
        </span>
        <span aria-hidden="true" className="hero-rise block text-3xl sm:text-4xl md:text-5xl lg:text-6xl" style={{ animationDelay: '60ms' }}>
          Tu <span className="font-fancy">link</span> para
        </span>
        <span aria-hidden="true" className="hero-rise block mt-1 sm:mt-2 text-[2.35rem] sm:text-5xl lg:text-7xl" style={{ animationDelay: '170ms' }}>
          <RotatingWords
            className="bg-gradient-to-r from-cyan-300 via-emerald-300 to-green-400 bg-clip-text text-transparent pb-[0.1em]"
            words={PALABRAS_TITULAR}
            onChange={setPalabraActiva}
          />
        </span>
      </h1>

      {/* Subtítulo. Las frases y el esquema van en la misma celda
          (.hero-cambio): el lugar queda reservado para el más alto desde
          el principio, y nada de abajo se mueve cuando uno reemplaza al
          otro. Cada uno lleva su "Vos solo atendés" abajo: con las
          frases, pegado como siempre; con el esquema, aparece al final
          del armado, como remate. Las frases siguen ahí, invisibles,
          para lectores de pantalla; el esquema es decorativo. */}
      <div
        className="hero-rise text-lg sm:text-xl md:text-2xl leading-relaxed mb-8 sm:mb-10"
        style={{ animationDelay: '280ms' }}
      >
        <div className={`hero-cambio${conEsquema ? ' con-esquema' : ''}`}>
          <p className="hero-frases text-slate-300 max-w-2xl mx-auto px-4">
            <span className="texto-balanceado block">
              {FRASES_SUBTITULO.map((f, n) => (
                <React.Fragment key={f.texto}>
                  {n > 0 && (n === FRASES_SUBTITULO.length - 1 ? ' e ' : ', ')}
                  <span className={`frase-viva${fraseActiva === f.palabra || fraseActiva === PALABRA_TODO ? ' is-activa' : ''}`}>
                    {f.texto}
                  </span>
                </React.Fragment>
              ))}
              {', '}
              <span className={`frase-viva${fraseActiva === PALABRA_TODO ? ' is-activa' : ''}`}>todo desde tu link</span>.
            </span>
            <span className="block mt-2 text-white font-semibold">Vos solo atendés.</span>
          </p>
          <div className="hero-esquema" aria-hidden="true">
            <EsquemaHero visible={conEsquema} activas={piezasActivas} />
            <p className="hero-esquema-pie mt-4 px-4 text-white font-semibold">Vos solo atendés.</p>
          </div>
        </div>
      </div>
    </>
  );
};

const Hero = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Función para verificar el estado de login
  const checkLoginStatus = () => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    
    if (userData && loginStatus === 'true') {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsLoggedIn(true);
      } catch (error) {
        localStorage.removeItem('user');
        localStorage.removeItem('isLoggedIn');
        setUser(null);
        setIsLoggedIn(false);
      }
    } else {
      setUser(null);
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    setIsVisible(true);
    checkLoginStatus();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'isLoggedIn') {
        checkLoginStatus();
      }
    };

    const handleLogout = () => {
      checkLoginStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogout', handleLogout);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogout', handleLogout);
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      checkLoginStatus();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Si ya está logueado, ir directo al panel correspondiente (no mostrar la home)
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    if (user.es_profesional) navigate('/dashboard/profesional', { replace: true });
    else if (user.isAssistant || user.is_assistant) navigate('/assistant/dashboard', { replace: true });
    else navigate('/dashboard/paciente', { replace: true });
  }, [isLoggedIn, user, navigate]);

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleSearchProfessionals = () => {
    navigate('/search');
  };

  // Si el usuario está logueado, mostramos un loader breve mientras el
  // useEffect de arriba lo redirige a su panel.
  if (isLoggedIn) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-green-50">
        <LoadingSpinner size={52} label="Abriendo tu panel..." />
      </section>
    );
  }

  // Timeline steps data
  const timelineSteps = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      title: "Tu página profesional",
      subtitle: "consultarapp.com/tu-nombre",
      description: "Portfolio con tus servicios, precios y fotos. Compartilo en redes.",
      gradient: "from-blue-500 to-cyan-500",
      delay: "0ms"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: "Agenda inteligente",
      subtitle: "Turnos online 24/7",
      description: "Tus pacientes reservan solos. Vos definís horarios y duración.",
      gradient: "from-cyan-500 to-teal-500",
      delay: "150ms"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      title: "Cobros con MercadoPago",
      subtitle: "Señas del 10% al 50%",
      description: "Cobrá por adelantado con tarjeta o verificá transferencias.",
      gradient: "from-teal-500 to-green-500",
      delay: "300ms"
    },
    {
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
      title: "WhatsApp automático",
      subtitle: "Integración oficial Meta",
      description: "Confirmación instantánea + recordatorio 24hs antes. Sin hacer nada.",
      gradient: "from-green-500 to-emerald-500",
      delay: "450ms",
      highlight: true
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "Emails profesionales",
      subtitle: "Confirmaciones y recordatorios",
      description: "Tus pacientes reciben todo por email también. Doble cobertura.",
      gradient: "from-emerald-500 to-cyan-500",
      delay: "600ms"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Reportes en tiempo real",
      subtitle: "Ingresos, tendencias y métricas",
      description: "Visualizá tus datos con gráficos. Tomá decisiones informadas.",
      gradient: "from-cyan-500 to-blue-500",
      delay: "750ms"
    }
  ];

  // Hero para usuarios NO logueados
  return (
    <>
      {/* Hero Section Principal */}
      <section className="relative min-h-screen flex items-start md:items-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Fondo: auroras + la cinta de luz, que sigue al cursor y sube con el
            scroll (CintaDeLuz). El velo oscuro detrás del texto está para que
            la cinta nunca le quite contraste al titular. */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Fondo vivo: manchas de luz con los colores del logo (celeste y
              verde) que se desplazan lento (ver .hero-mancha en Hero.css) */}
          <div className="hero-mancha hero-mancha--celeste"></div>
          <div className="hero-mancha hero-mancha--verde"></div>
          <div className="hero-mancha hero-mancha--cian"></div>
          {/* Grano/ruido sutil, debajo de la cinta (ver .hero-noise) */}
          <div className="hero-noise"></div>
          <CintaDeLuz className="absolute inset-0 w-full h-full" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 65% 42% at 50% 40%, rgba(2, 6, 23, 0.55), transparent 72%)' }}
          />
        </div>

        <div className="relative w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-10 sm:pb-16 md:pt-12 md:pb-20">

          {/* Contenido principal */}
          <div className="text-center mb-12 sm:mb-16">

            {/* Prueba social arriba de todo: lo primero que se ve */}
            <ProfessionalsShowcase />

            {/* Titular y subtítulo (ver MensajeDelHero, arriba) */}
            <MensajeDelHero />

            {/* Botones CTA */}
            <div className="hero-rise flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 px-4" style={{ animationDelay: '390ms' }}>
              <button
                onClick={handleGetStarted}
                className="hero-cta-brillo relative overflow-hidden w-full sm:w-auto group bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 sm:px-10 md:px-12 py-4 sm:py-5 rounded-2xl font-semibold text-base sm:text-lg hover:shadow-2xl hover:shadow-green-500/30 hover:scale-105 transition-all duration-300"
              >
                <span className="flex items-center justify-center space-x-2">
                  <span>Empezar gratis</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
              
              <button
                onClick={handleSearchProfessionals}
                // Fondo oscuro semitransparente y no backdrop-blur: la cinta de
                // luz pasa por detrás, y un desenfoque de fondo se recalcularía
                // en cada cuadro de la animación.
                className="w-full sm:w-auto group bg-slate-900/50 text-white border border-white/20 px-8 sm:px-10 md:px-12 py-4 sm:py-5 rounded-2xl font-semibold text-base sm:text-lg hover:bg-white/15 hover:border-white/40 transition-all duration-300"
              >
                <span className="flex items-center justify-center space-x-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Ver profesionales</span>
                </span>
              </button>
            </div>

            {/* Stats rápidos */}
            <div className="hero-rise flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-10 text-sm text-gray-400 px-4" style={{ animationDelay: '500ms' }}>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Recordatorios por WhatsApp</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                <span>MercadoPago integrado</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                <span>Recetas electrónicas</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span>Configuración en 5 min</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Timeline Section - EL CAMINITO */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Todo automatizado,
              <span className="bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent"> paso a paso</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Desde que un paciente te encuentra hasta que llega a su turno
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Línea vertical central - solo desktop */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-500 via-green-500 to-emerald-500 rounded-full"></div>

            {/* Steps */}
            <div className="space-y-8 md:space-y-0">
              {timelineSteps.map((step, index) => (
                <div 
                  key={index}
                  className={`relative flex flex-col md:flex-row items-center md:items-stretch gap-4 md:gap-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ animationDelay: step.delay }}
                >
                  {/* Content - alterna izquierda/derecha en desktop */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:order-2 md:text-left md:pl-12'}`}>
                    <div className={`
                      relative p-6 rounded-2xl backdrop-blur-sm border transition-all duration-300 hover:scale-105
                      ${step.highlight 
                        ? 'bg-green-500/20 border-green-500/50 shadow-lg shadow-green-500/20' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }
                    `}>
                      {step.highlight && (
                        <div className="absolute -top-3 left-4 md:left-auto md:right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          <Star className="w-3 h-3 inline mr-1" /> NUEVO
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-white mb-1">{step.title}</h3>
                      <p className="text-sm text-cyan-400 font-medium mb-2">{step.subtitle}</p>
                      <p className="text-gray-400">{step.description}</p>
                    </div>
                  </div>

                  {/* Icon circle - centro */}
                  <div className={`
                    relative z-10 flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg
                    bg-gradient-to-br ${step.gradient}
                    ${index % 2 === 0 ? '' : 'md:order-1'}
                  `}>
                    {step.icon}
                    {/* Connector dots for mobile */}
                    {index < timelineSteps.length - 1 && (
                      <div className="md:hidden absolute -bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-1">
                        <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                        <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                        <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                      </div>
                    )}
                  </div>

                  {/* Empty space for alternating layout */}
                  <div className={`hidden md:block flex-1 ${index % 2 === 0 ? 'md:order-2' : ''}`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sección: Listo en 5 minutos */}
      <OnboardingSteps />

      {/* Sección: Features detalladas con mockups */}
      <FeaturesDetailed />

      {/* Sección: Trabajá con orden */}
      <EmotionalSection />

      {/* NUEVA SECCIÓN: Historias Clínicas Digitales */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 overflow-hidden">
        {/* Fondo simplificado */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-slate-900/20"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center space-x-2 bg-indigo-500/20 backdrop-blur-sm border border-indigo-400/50 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-6">
              <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm sm:text-base font-semibold text-indigo-400">
                <Sparkles className="w-4 h-4 inline mr-1" /> Nueva función exclusiva
              </span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Historias Clínicas
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent"> Digitales Seguras</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Gestiona toda la información de tus pacientes de forma organizada y protegida
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: <KeyRound className="w-8 h-8 text-indigo-400" />,
                title: "Cifrado de extremo a extremo",
                description: "Toda la información médica encriptada según normativas de protección de datos."
              },
              {
                icon: <ClipboardList className="w-8 h-8 text-indigo-400" />,
                title: "Plantillas personalizables",
                description: "Crea tus propios formatos de historia clínica según tu especialidad."
              },
              {
                icon: <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>,
                title: "Archivos adjuntos",
                description: "Adjunta análisis, imágenes, recetas y cualquier documento relevante."
              },
              {
                icon: <Bell className="w-8 h-8 text-indigo-400" />,
                title: "Alertas inteligentes",
                description: "Notificaciones para seguimientos, controles y recordatorios importantes."
              },
              {
                icon: <BarChart3 className="w-8 h-8 text-indigo-400" />,
                title: "Dashboard de paciente",
                description: "Vista completa de cada paciente con historial, tratamientos y evolución."
              },
              {
                icon: <Users className="w-8 h-8 text-indigo-400" />,
                title: "Acceso multiusuario",
                description: "Comparte acceso con colegas o asistentes manteniendo controles de privacidad."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* CTA para historias clínicas */}
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 backdrop-blur-sm border border-indigo-400/30 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">¿Listo para digitalizar tus historias clínicas?</h3>
              <p className="text-gray-300 mb-6">Incluido en tu plan sin costo adicional. Totalmente integrado con tu agenda.</p>
              <button
                onClick={handleGetStarted}
                className="group bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:shadow-2xl hover:shadow-indigo-500/30 hover:scale-105 transition-all duration-300"
              >
                <span className="flex items-center justify-center space-x-2">
                  <span>Probar historias clínicas</span>
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Recetas incluidas y todo enlazado: lleva a la tarjeta de precio */}
      <TodoConectado
        precio={PRECIO_MENSUAL}
        alVerPlan={() => document.getElementById('plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />

      {/* Pricing Section - SIMPLIFICADO */}
      <section id="plan" className="relative py-20 sm:py-28 bg-slate-900 overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Pricing Card */}
          <div className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="relative bg-slate-800 rounded-3xl shadow-2xl overflow-hidden border border-slate-700">
              
              {/* Header gradient */}
              <div className="bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 p-8 text-center text-white">
                <div className="inline-flex items-center space-x-2 bg-white/20 rounded-full px-4 py-2 mb-4">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                  </span>
                  <span className="text-sm font-semibold">Precio único</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold mb-2">{PRECIO_MENSUAL} <span className="text-xl font-normal opacity-80">/mes</span></h2>
                <p className="text-white/80">Todo incluido. Sin sorpresas.</p>
              </div>

              {/* Features grid */}
              <div className="p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: <Globe className="w-6 h-6 text-blue-400" />, text: "Tu página profesional" },
                    { icon: <CalendarDays className="w-6 h-6 text-cyan-400" />, text: "Agenda ilimitada" },
                    { icon: <CreditCard className="w-6 h-6 text-green-400" />, text: "MercadoPago integrado" },
                    { icon: <MessageSquare className="w-6 h-6 text-emerald-400" />, text: "WhatsApp automático" },
                    { icon: <Mail className="w-6 h-6 text-teal-400" />, text: "Emails de recordatorio" },
                    { icon: <ClipboardList className="w-6 h-6 text-indigo-400" />, text: "Historias clínicas" },
                    { icon: <Pill className="w-6 h-6 text-emerald-400" />, text: "Recetas electrónicas" },
                    { icon: <MapPin className="w-6 h-6 text-sky-400" />, text: "Varias sedes" },
                    { icon: <Lock className="w-6 h-6 text-purple-400" />, text: "Datos encriptados" },
                    { icon: <Headphones className="w-6 h-6 text-pink-400" />, text: "Soporte por WhatsApp" },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 rounded-xl bg-slate-700/50 hover:bg-slate-700 transition-colors">
                      {feature.icon}
                      <span className="text-gray-200 font-medium">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="text-center">
                  <button
                    onClick={handleGetStarted}
                    className="w-full sm:w-auto group bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 text-white px-12 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <span>Comenzar ahora</span>
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </button>
                  <p className="text-sm text-gray-400 mt-4">
                    Sin compromiso • Cancela cuando quieras • Soporte incluido
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust elements */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">Integraciones oficiales con:</p>
            <div className="flex flex-wrap items-center justify-center gap-8">
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold text-[#00a650]">MercadoPago</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <svg className="w-8 h-8 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="font-semibold">WhatsApp Business</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <QuickFAQ />
    </>
  );
};

export default Hero;